import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import {
  getFilesFileIDDownloadUrl,
  type PartnerMediaFileListItemDto,
  PartnerMediaFileSortColumn,
  SortOrder,
} from "Infrastructure/Api/Api";
import { isSuccessResponse } from "Infrastructure/Api/ApiUtils";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useDeleteMediaFileMutation } from "MusicAudio/Api/Mutations/useDeleteMediaFileMutation";
import { useMediaFilesQuery } from "MusicAudio/Api/Queries/useMediaFilesQuery";
import {
  MEDIA_FILES_SORTABLE_COLUMNS,
  useMediaFilesTableColumns,
} from "MusicAudio/Components/useMediaFilesTableColumns";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlConfirmDialog } from "Shared/Dialogs/BlConfirmDialog";

const SORT_COLUMN_MAP: Record<string, PartnerMediaFileSortColumn> = {
  displayName: PartnerMediaFileSortColumn.DisplayName,
  fileName: PartnerMediaFileSortColumn.FileName,
  durationSeconds: PartnerMediaFileSortColumn.Duration,
  dateCreated: PartnerMediaFileSortColumn.DateCreated,
};

export const MediaFilesTable = (): React.ReactNode => {
  const { t } = useResource();

  const [deleteTarget, setDeleteTarget] =
    useState<PartnerMediaFileListItemDto | null>(null);

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    sorting,
    onSortingChange,
  } = useTableSearchParams({
    validSortIds: [...MEDIA_FILES_SORTABLE_COLUMNS],
    defaultSort: { id: "dateCreated", desc: true },
  });

  const deleteMutation = useDeleteMediaFileMutation();

  const sortBy = sorting[0]?.id
    ? SORT_COLUMN_MAP[sorting[0].id]
    : PartnerMediaFileSortColumn.DateCreated;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data: mediaFilesData, isLoading } = useMediaFilesQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery: debouncedSearch || undefined,
    sortBy,
    sortOrder,
  });

  const mediaFiles = mediaFilesData?.items ?? [];
  const totalCount = mediaFilesData?.totalCount ?? 0;

  const handleDeleteConfirm = async (confirmed: boolean) => {
    setDeleteTarget(null);
    if (!confirmed || !deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.partnerMediaFileID);
      toast.success(t(Resources.MusicAudio.DeleteDialog.SuccessMessage));
    } catch {
      toast.error(t(Resources.MusicAudio.DeleteDialog.ErrorMessage));
    }
  };

  const handleDownload = useCallback(
    async (file: PartnerMediaFileListItemDto) => {
      try {
        const response = await getFilesFileIDDownloadUrl(file.fileID);
        if (isSuccessResponse(response)) {
          const a = document.createElement("a");
          a.href = response.data.downloadUrl;
          a.download = file.displayName ?? file.fileName;
          a.click();
        } else {
          toast.error(t(Resources.MusicAudio.DownloadError));
        }
      } catch {
        toast.error(t(Resources.MusicAudio.DownloadError));
      }
    },
    [t],
  );

  const columns = useMediaFilesTableColumns(handleDownload, setDeleteTarget);

  const table = useReactTable({
    data: mediaFiles,
    columns,
    getRowId: (row) => row.partnerMediaFileID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  return (
    <Box>
      <Stack sx={{ marginBottom: Spacing.M }}>
        <Box sx={{ width: "100%" }}>
          <TableSearchInput
            name="media-file-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.MusicAudio.SearchPlaceholder)}
          />
        </Box>
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
      />
      <BlConfirmDialog
        isOpen={Boolean(deleteTarget)}
        handleResult={handleDeleteConfirm}
        modalTitle={t(Resources.MusicAudio.DeleteDialog.Title)}
        modalContent={t(Resources.MusicAudio.DeleteDialog.Content)}
        confirmButtonText={t(Resources.MusicAudio.DeleteDialog.Confirm)}
      />
    </Box>
  );
};
